import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const GuideWrapper = styled.div`
  background-color: var(--darkBG);
  color: var(--lightText);
  min-height: 100vh;
  font-family: 'Outfit', sans-serif;
  overflow-x: hidden;
  padding-bottom: 80px;
`;

export const Hero = styled.section`
  position: relative;
  width: 100%;
  padding: 100px 20px 60px;
  text-align: center;
  background: linear-gradient(180deg, rgba(10, 10, 10, 0.6) 0%, rgba(10, 10, 10, 0.95) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .back-btn-container {
    max-width: 1200px;
    width: 90%;
    text-align: left;
    margin-bottom: 30px;
  }
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 8px 18px;
  color: #cfc9be;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(212, 175, 55, 0.08);
    border-color: var(--accentGold);
    color: #ffffff;
    transform: translateX(-4px);
  }
`;

export const Title = styled.h1`
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #ffffff 30%, var(--accentGold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.15;
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const Subtitle = styled.p`
  font-size: clamp(1rem, 2vw, 1.25rem);
  color: #cfc9be;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.9;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

export const Container = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

export const Introduction = styled.div`
  margin: 60px auto 40px;
  text-align: center;
  font-size: 1.15rem;
  line-height: 1.8;
  color: #d1d5db;
  max-width: 850px;
`;

export const InteractiveSpecContainer = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--cardBorder);
  border-radius: 24px;
  padding: 40px;
  margin: 40px 0 60px;
  backdrop-filter: blur(10px);
  box-shadow: 0 15px 45px rgba(0, 0, 0, 0.35);
  text-align: center;

  h2 {
    font-size: var(--fontMed);
    color: #ffffff;
    margin-bottom: 30px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

export const SpecDisplay = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: clamp(2rem, 6vw, 3.8rem);
  font-weight: 800;
  font-family: monospace;
  margin-bottom: 40px;
`;

export const SpecToken = styled.span`
  color: ${({ color }) => color || '#ffffff'};
  padding: 4px 12px;
  border-radius: 8px;
  background: ${({ active, color }) => active ? `rgba(${active}, 0.15)` : 'transparent'};
  border: 1px solid ${({ active, color }) => active ? color : 'transparent'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: ${({ color }) => `rgba(255, 255, 255, 0.05)`};
    transform: scale(1.05);
  }
`;

export const TokenDetailCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${({ borderColor }) => borderColor || 'var(--cardBorder)'};
  border-radius: 16px;
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.5s ease-out;

  h3 {
    font-size: 1.3rem;
    color: ${({ headerColor }) => headerColor || '#ffffff'};
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  p {
    color: #cfc9be;
    line-height: 1.6;
    margin: 0;
  }
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.2rem);
  color: #ffffff;
  margin-top: 60px;
  margin-bottom: 30px;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 5px;
    bottom: 5px;
    width: 5px;
    background: var(--accentGold);
    border-radius: 4px;
    box-shadow: 0 0 10px var(--accentGold);
  }
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 60px;
`;

export const SpecCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1.5px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 30px;
  transition: all 0.4s ease;

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .number-badge {
    background: ${({ color }) => color || 'var(--accentGold)'};
    color: #000000;
    font-weight: 800;
    font-size: 1.3rem;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: ${({ color }) => `0 4px 15px rgba(255, 255, 255, 0.1)`};
  }

  h3 {
    font-size: 1.2rem;
    color: #ffffff;
    margin: 0;
    font-weight: 700;
  }

  p {
    font-size: 0.95rem;
    color: #a1a1aa;
    line-height: 1.6;
    margin: 0;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ color }) => color || 'var(--accentGold)'};
    box-shadow: ${({ color }) => color ? `0 10px 30px rgba(${color}, 0.1)` : '0 10px 30px rgba(212, 175, 55, 0.15)'};
  }
`;

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: rgba(255, 255, 255, 0.01);
  border: 1px solid var(--cardBorder);
  border-radius: 16px;
  margin: 24px 0 40px;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  min-width: 500px;

  th, td {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  th {
    background: rgba(255, 255, 255, 0.03);
    color: var(--accentGold);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.88rem;
    letter-spacing: 0.5px;
  }

  td {
    color: #cfc9be;
    font-size: 0.95rem;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.01);
  }
`;

export const InfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 60px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

export const InfoBlock = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--cardBorder);
  border-radius: 20px;
  padding: 30px;

  h3 {
    font-size: 1.25rem;
    color: var(--accentGold);
    margin-bottom: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  p {
    font-size: 0.95rem;
    color: #cfc9be;
    line-height: 1.6;
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 12px;
      display: flex;
      align-items: flex-start;
      color: #cfc9be;
      font-size: 0.95rem;

      &::before {
        content: "•";
        color: var(--accentGold);
        font-weight: bold;
        display: inline-block;
        width: 1em;
        margin-left: -0.5em;
      }
    }
  }
`;

export const HeroWarningBox = styled.div`
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.25);
  border-left: 5px solid #ef4444;
  border-radius: 12px;
  padding: 16px 24px;
  margin: 30px auto 0;
  max-width: 750px;
  width: 90%;
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.1);
  backdrop-filter: blur(8px);
  animation: ${fadeInUp} 0.8s ease-out 0.3s both;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(239, 68, 68, 0.5);
    box-shadow: 0 0 25px rgba(239, 68, 68, 0.2);
    transform: translateY(-2px);
  }

  .warning-icon {
    flex-shrink: 0;
    color: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .warning-text {
    font-size: 0.98rem;
    line-height: 1.5;
    color: #cfc9be;

    strong {
      color: #ffffff;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
    padding: 18px;
    gap: 12px;
  }
`;


export const CTASection = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, rgba(18, 16, 14, 0.85) 100%);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 28px;
  padding: 50px 40px;
  text-align: center;
  margin-top: 60px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);

  h3 {
    font-size: 1.8rem;
    color: #ffffff;
    margin-bottom: 12px;
    font-weight: 800;
    text-transform: uppercase;
  }

  p {
    color: #cfc9be;
    font-size: 1.05rem;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto 30px auto;
  }

  .cta-buttons {
    display: flex;
    gap: 20px;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0b0f19;
  font-weight: 700;
  font-size: 0.95rem;
  border-radius: 30px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(212, 175, 55, 0.35);
  }
`;

export const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: transparent;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #ffffff;
    transform: translateY(-3px);
  }
`;

export const TwiImageGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.22fr;
  gap: 24px;
  margin: 30px 0 50px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 20px 0 40px;
  }
`;

export const TwiImageCard = styled.div`
  background: ${({ lightBg }) => lightBg ? '#ffffff' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.3s ease;
  height: 100%;
  box-sizing: border-box;
  cursor: pointer;

  .image-title {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${({ lightBg }) => lightBg ? '#0b0f19' : 'var(--accentGold)'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    flex: 1;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(212, 175, 55, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);

    img {
      transform: scale(1.02);
    }
  }
`;

export const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const LightboxContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: zoomIn 0.3s ease forwards;

  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  @keyframes zoomIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: -45px;
  right: -10px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 2.5rem;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;

  &:hover {
    color: var(--accentGold);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: -45px;
    right: 5px;
  }
`;

export const LightboxTitle = styled.div`
  position: absolute;
  bottom: -40px;
  left: 50%;
  transform: translateX(-50%);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
  width: 100%;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
`;

export const SpecExplorerContent = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: center;
  margin-top: 30px;
  flex-direction: row;

  @media (max-width: 968px) {
    flex-direction: column;
    gap: 30px;
  }
`;

export const TyreVisualContainer = styled.div`
  position: relative;
  width: 380px;
  height: 380px;
  perspective: 1000px;
  animation: ${fadeInUp} 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;

  @media (max-width: 480px) {
    width: 300px;
    height: 300px;
  }
`;

export const WheelCardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ isFlipped }) => isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

export const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 15px 35px rgba(0, 0, 0, 0.6));
  }

  .interactive-marking {
    cursor: pointer;
    font-weight: 800;
    font-family: 'Outfit', sans-serif;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    fill: #a1a1aa;

    &:hover {
      fill: #ffffff;
      filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.8));
    }
  }

  .dim-line {
    animation: fadeIn 0.4s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export const SideViewFace = styled(CardFace)`
  transform: rotateY(0deg);
`;

export const FrontViewFace = styled(CardFace)`
  transform: rotateY(180deg);
  cursor: pointer;

  &:hover {
    svg {
      filter: drop-shadow(0 15px 35px rgba(59, 130, 246, 0.25));
    }
  }
`;


export const SpecDetailsContainer = styled.div`
  flex: 1;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
  
  @media (max-width: 968px) {
    width: 100%;
    align-items: center;
    text-align: center;
  }
`;



